import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Button, Upload, message, Row, Col, Spin, Space, Tabs, Card, Checkbox } from 'antd';
import type { UploadFile, UploadFileStatus } from 'antd/es/upload/interface';
import apiClient from '../services/api';
import dayjs from 'dayjs';
import SupplierModal from '../components/SupplierModal';
import LocationModal from '../components/LocationModal';
import AssetCategoryModal from '../components/AssetCategoryModal';
import EmployeeModal from '../components/EmployeeModal';
import AssetAccessories from '../components/AssetAccessories';
import type { Supplier, Employee, Asset } from '../types';
import axios from 'axios';
import { 
  MinusCircleOutlined, 
  PlusOutlined, 
  InfoCircleOutlined, 
  SettingOutlined, 
  ToolOutlined, 
  PaperClipOutlined,
  UserOutlined,
  ShopOutlined,
  AppstoreAddOutlined
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { 
  processTechnicalSpecifications, 
  formatDateForAPI, 
  getTabMapForValidation,
  filterListByCompany 
} from '../utils/assetFormUtils';

const defaultTechFields = [
  { key: 'Thương hiệu', value: '' },
  { key: 'Model', value: '' },
  { key: 'Thông tin mô tả', value: '' },
  { key: 'Tiêu thụ', value: '0' },
  { key: 'Năng suất', value: '0' },
];

const defaultLabels = [
  'Thương hiệu',
  'Model',
  'Thông tin mô tả',
  'Tiêu thụ',
  'Năng suất',
];

// Props: truyền assetId và các danh sách từ component cha
interface LocationItem {
  id: number;
  name: string;
  company_id: number;
}
interface EmployeeItem extends Employee {
  company_id: number;
}
interface AssetEditFormProps {
  assetId: number;
  companyList: { id: number; name: string }[];
  categoryList: { id: number; name: string }[];
  locationList: LocationItem[];
  supplierList: Supplier[];
  employeeList: EmployeeItem[];
  onSuccess: () => void;
  onCancel: () => void;
  onSupplierCreated?: () => void;
  onLocationCreated?: () => void;
  onCategoryCreated?: () => void;
  onEmployeeCreated?: () => void;
}

function AssetEditForm(props: AssetEditFormProps): React.ReactElement {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [asset, setAsset] = useState<any>(null);
  const [imageFiles, setImageFiles] = useState<UploadFile[]>([]);
  const [docFiles, setDocFiles] = useState<UploadFile[]>([]);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [currentSupplierList, setCurrentSupplierList] = useState<Supplier[]>(props.supplierList);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [currentLocationList, setCurrentLocationList] = useState<LocationItem[]>(props.locationList);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [currentCategoryList, setCurrentCategoryList] = useState(props.categoryList);
  const [employeeModalVisible, setEmployeeModalVisible] = useState(false);
  const [currentEmployeeList, setCurrentEmployeeList] = useState<EmployeeItem[]>(props.employeeList);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  const { user } = useAuth();

  // --- UPLOAD ẢNH & TÀI LIỆU ---
  // Lưu thông tin file ảnh đã upload
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  // Thêm state lưu id file cần xóa
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);

  // Xử lý upload ảnh instant
  const handleImageUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append('file', file as Blob);
    if (form.getFieldValue('company_id')) formData.append('company_id', form.getFieldValue('company_id'));
    if (form.getFieldValue('asset_code')) formData.append('asset_code', form.getFieldValue('asset_code'));
    if (props.assetId) formData.append('asset_id', String(props.assetId));
    if (user?.id) formData.append('uploaded_by_user_id', String(user.id));
    
    // Lấy token từ localStorage
    const token = localStorage.getItem('authToken');
    const headers: any = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    try {
      const res = await axios.post(`${API_URL}/asset-attachments/upload`, formData, {
        headers,
        onUploadProgress: (event) => {
          if (event.total) {
            onProgress?.({ percent: Math.round((event.loaded / event.total) * 100) });
          }
        },
      });
      setUploadedImages((prev: any[]) => [...prev, res.data]);
      onSuccess?.({ ...res.data, url: res.data.file_path, name: res.data.file_name, response: res.data }, file);
    } catch (err) {
      onError?.(err as any);
    }
  };

  // Xử lý upload tài liệu instant
  const handleDocUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append('file', file as Blob);
    if (form.getFieldValue('company_id')) formData.append('company_id', form.getFieldValue('company_id'));
    if (form.getFieldValue('asset_code')) formData.append('asset_code', form.getFieldValue('asset_code'));
    if (props.assetId) formData.append('asset_id', String(props.assetId));
    if (user?.id) formData.append('uploaded_by_user_id', String(user.id));
    
    // Lấy token từ localStorage
    const token = localStorage.getItem('authToken');
    const headers: any = { 'Content-Type': 'multipart/form-data' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    try {
      const res = await axios.post(`${API_URL}/asset-attachments/upload`, formData, {
        headers,
        onUploadProgress: (event) => {
          if (event.total) {
            onProgress?.({ percent: Math.round((event.loaded / event.total) * 100) });
          }
        },
      });
      setUploadedDocs((prev: any[]) => [...prev, res.data]);
      onSuccess?.({ ...res.data, url: res.data.file_path, name: res.data.file_name, response: res.data }, file);
    } catch (err) {
      onError?.(err as any);
    }
  };

  // Xử lý xóa ảnh instant
  const handleRemoveImage = async (file: UploadFile) => {
    try {
      // Nếu file có uid (đã có trong DB), xóa qua API
      if (file.uid && !isNaN(Number(file.uid))) {
        await apiClient.deleteAssetAttachment(Number(file.uid));
        setDeletedAttachmentIds(prev => [...prev, Number(file.uid)]);
      } 
      // Nếu file không có uid (vừa upload instant), xóa file vật lý
      else if (file.response?.file_path) {
        const token = localStorage.getItem('authToken');
        const headers: any = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: file.response.file_path }
        });
      }
      
      // Xóa khỏi state
      setImageFiles(prev => prev.filter(f => f.uid !== file.uid));
      setUploadedImages(prev => prev.filter(f => f.file_path !== file.response?.file_path));
      
      message.success('Đã xóa ảnh thành công');
    } catch (err) {
      message.error('Lỗi xóa ảnh');
    }
    return true;
  };

  // Xử lý xóa tài liệu instant
  const handleRemoveDoc = async (file: UploadFile) => {
    try {
      // Nếu file có uid (đã có trong DB), xóa qua API
      if (file.uid && !isNaN(Number(file.uid))) {
        await apiClient.deleteAssetAttachment(Number(file.uid));
        setDeletedAttachmentIds(prev => [...prev, Number(file.uid)]);
      } 
      // Nếu file không có uid (vừa upload instant), xóa file vật lý
      else if (file.response?.file_path) {
        const token = localStorage.getItem('authToken');
        const headers: any = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
          headers,
          data: { file_path: file.response.file_path }
        });
      }
      
      // Xóa khỏi state
      setDocFiles(prev => prev.filter(f => f.uid !== file.uid));
      setUploadedDocs(prev => prev.filter(f => f.file_path !== file.response?.file_path));
      
      message.success('Đã xóa tài liệu thành công');
    } catch (err) {
      message.error('Lỗi xóa tài liệu');
    }
    return true;
  };

  // Load asset detail khi mount
  useEffect(() => {
    const fetchAsset = async () => {
      setLoading(true);
      try {
        const data = await apiClient.getAsset(props.assetId);
        setAsset(data);
        // Debug logs để kiểm tra dữ liệu
        
        
        // Xử lý technical_specifications về dạng mảng cho Form.List
        let techArr: string[] = [];
        if (data.technical_specifications && Object.keys(data.technical_specifications).length > 0) {
          const spec = data.technical_specifications;
  
          techArr = [
            spec['Thương hiệu'] || '',
            spec['Model'] || '',
            spec['Thông tin mô tả'] || '',
            spec['Tiêu thụ'] !== undefined ? String(spec['Tiêu thụ']) : '0',
            spec['Năng suất'] !== undefined ? String(spec['Năng suất']) : '0',
          ];
        } else {
  
          // Luôn có 5 trường mặc định
          techArr = [
            '', // Thương hiệu
            '', // Model  
            '', // Thông tin mô tả
            '0', // Tiêu thụ
            '0', // Năng suất
          ];
        }
        const purchaseDateObj = data.purchase_date ? dayjs(data.purchase_date) : null;
        const warrantyExpiryDateObj = data.warranty_expiry_date ? dayjs(data.warranty_expiry_date) : null;
        const lastAuditDateObj = data.last_audit_date ? dayjs(data.last_audit_date) : null;
        
        // Set form values trực tiếp (không bao gồm technical_specifications)
        const formValues = {
          ...data,
          purchase_date: dayjs.isDayjs(purchaseDateObj) && purchaseDateObj.isValid() ? purchaseDateObj : null,
          warranty_expiry_date: dayjs.isDayjs(warrantyExpiryDateObj) && warrantyExpiryDateObj.isValid() ? warrantyExpiryDateObj : null,
          last_audit_date: dayjs.isDayjs(lastAuditDateObj) && lastAuditDateObj.isValid() ? lastAuditDateObj : null,
          // Không set technical_specifications ở đây vì đã được set trong initialValues
          // Đảm bảo description được set đúng
          description: data.description || '',
          // Đảm bảo purchase_cost được set đúng
          purchase_cost: data.purchase_cost || 0,
        };
        

        
        form.setFieldsValue(formValues);
        

        
        // Set technical_specifications sau khi asset được load
        if (techArr && techArr.length > 0) {
          setTimeout(() => {
            form.setFieldsValue({
              technical_specifications: techArr
            });
            
          }, 200);
        }
        

        

        // Set ảnh và tài liệu hiện có
        if (data.attachments) {
          const images = data.attachments
            .filter((att: any) => att.attachment_type === 'image')
            .map((att: any) => ({
              uid: att.id,
              name: att.file_name,
              status: 'done' as UploadFileStatus,
              url: att.file_path,
            }));
          setImageFiles(images);
          const documents = data.attachments
            .filter((att: any) => att.attachment_type === 'document')
            .map((att: any) => ({
              uid: att.id,
              name: att.file_name,
              status: 'done' as UploadFileStatus,
              url: att.file_path,
            }));
          setDocFiles(documents);
        }
        // BỔ SUNG: Xử lý dữ liệu bảo dưỡng
        if (data.maintenance_schedules && Array.isArray(data.maintenance_schedules)) {
          setSelectedMaintenanceTypes(data.maintenance_schedules.map((s: any) => s.maintenance_type_id));
          const cycles: Record<string, number> = {};
          data.maintenance_schedules.forEach((s: any) => {
            cycles[s.maintenance_type_id] = s.cycle_months;
          });
          setMaintenanceCycles(cycles);
        } else {
          setSelectedMaintenanceTypes([]);
          setMaintenanceCycles({});
        }
        
        // Load accessories ban đầu
        try {
          const accessories = await apiClient.getAssetAccessories(props.assetId);
          setAccessoriesList(accessories);
          // Chỉ update purchase_cost nếu có accessories và purchase_cost hiện tại là 0
          if (accessories && accessories.length > 0) {
            const currentPurchaseCost = form.getFieldValue('purchase_cost') || 0;
            if (currentPurchaseCost === 0) {
              updateAssetValue(accessories);
            }
          }
        } catch (err) {
          // Silent error handling
        }
      } catch (err) {
        message.error('Không tải được dữ liệu tài sản!');
      } finally {
        setLoading(false);
      }
    };
    if (props.assetId) fetchAsset();
    // eslint-disable-next-line
  }, [props.assetId]);

  // Xử lý khi tạo nhà cung cấp mới thành công
  const handleSupplierCreated = (newSupplier: Supplier) => {
    setCurrentSupplierList((prev: Supplier[]) => [...prev, newSupplier]);
    form.setFieldsValue({ supplier_id: newSupplier.id });
    setSupplierModalVisible(false);
    // Gọi callback để cập nhật danh sách nhà cung cấp ở component cha
    if (props.onSupplierCreated) {
      props.onSupplierCreated();
    }
  };

  // Xử lý khi tạo vị trí mới thành công
  const handleLocationCreated = (newLocation: any) => {
    setCurrentLocationList((prev: any[]) => [...prev, newLocation]);
    form.setFieldsValue({ location_id: newLocation.id });
    setLocationModalVisible(false);
    // Gọi callback để cập nhật danh sách vị trí ở component cha
    if (props.onLocationCreated) {
      props.onLocationCreated();
    }
  };

  // Xử lý khi tạo loại tài sản mới thành công
  const handleCategoryCreated = (newCategory: any) => {
    setCurrentCategoryList((prev: any[]) => [...prev, newCategory]);
    form.setFieldsValue({ category_id: newCategory.id });
    setCategoryModalVisible(false);
    // Gọi callback để cập nhật danh sách loại tài sản ở component cha
    if (props.onCategoryCreated) {
      props.onCategoryCreated();
    }
  };

  // Xử lý khi tạo nhân viên mới thành công
  const handleEmployeeCreated = (newEmployee: Employee) => {
    setCurrentEmployeeList((prev: EmployeeItem[]) => [...prev, newEmployee]);
    form.setFieldsValue({ assigned_to_employee_id: newEmployee.id });
    setEmployeeModalVisible(false);
    // Gọi callback để cập nhật danh sách nhân viên ở component cha
    if (props.onEmployeeCreated) {
      props.onEmployeeCreated();
    }
  };

  // Xử lý hủy form: xóa tất cả file đã upload instant
  const handleCancel = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const headers: any = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Xóa tất cả ảnh đã upload instant
      for (const image of uploadedImages) {
        if (image.file_path) {
          try {
            await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
              headers,
              data: { file_path: image.file_path }
            });
          } catch (err) {
            // Silent error handling
          }
        }
      }

      // Xóa tất cả tài liệu đã upload instant
      for (const doc of uploadedDocs) {
        if (doc.file_path) {
          try {
            await axios.delete(`${API_URL}/asset-attachments/delete-by-path`, {
              headers,
              data: { file_path: doc.file_path }
            });
          } catch (err) {
            // Silent error handling
          }
        }
      }

      // Reset state
      setUploadedImages([]);
      setUploadedDocs([]);
      setImageFiles([]);
      setDocFiles([]);
      setDeletedAttachmentIds([]);

      // Gọi callback onCancel
      props.onCancel();
    } catch (err) {
      // Vẫn đóng form ngay cả khi có lỗi
      props.onCancel();
    }
  };

  // Xử lý submit
  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      // Xử lý ngày tháng
      const submitValues = { ...values };
      submitValues.purchase_date = formatDateForAPI(submitValues.purchase_date);
      submitValues.warranty_expiry_date = formatDateForAPI(submitValues.warranty_expiry_date);
      submitValues.last_audit_date = formatDateForAPI(submitValues.last_audit_date);

      
      // Xử lý technical_specifications về object
      let techSpecsArr = submitValues.technical_specifications || [];
      
      // Nếu submitValues.technical_specifications là undefined, thử lấy từ form
      if (!techSpecsArr || techSpecsArr.length === 0) {
        const formValues = form.getFieldValue('technical_specifications');
        techSpecsArr = formValues || [];
      }
      
      submitValues.technical_specifications = processTechnicalSpecifications(techSpecsArr);
      // BỔ SUNG: build lại mảng maintenance_schedules từ state
      const maintenance_schedules = selectedMaintenanceTypes.map(typeId => ({
        maintenance_type_id: typeId,
        cycle_months: maintenanceCycles[String(typeId)]
      }));
      submitValues.maintenance_schedules = maintenance_schedules;
      // Loại bỏ các trường null/undefined và has_accessories
      Object.keys(submitValues).forEach(key => {
        if (submitValues[key] === null || submitValues[key] === undefined || key === 'has_accessories' || key === 'last_audit_date') {
          delete submitValues[key];
        }
      });
      // 1. Gọi API update asset
      await apiClient.updateAsset(props.assetId, {
        ...submitValues,
        deleted_attachment_ids: deletedAttachmentIds,
        accessories: hasAccessories ? accessoriesList : [],
      });
      setDeletedAttachmentIds([]); // reset sau khi submit thành công
      message.success('Cập nhật tài sản thành công!');
      props.onSuccess();
    } catch (err) {
      message.error('Cập nhật tài sản thất bại!');
    } finally {
      setLoading(false);
    }
  };

  // Hàm kiểm tra object có phải là dayjs không
  function isDayjs(obj: any) {
    return obj && typeof obj === 'object' && typeof obj.isValid === 'function' && dayjs.isDayjs(obj);
  }

  useEffect(() => {
    const purchase = form.getFieldValue('purchase_date');
    const warranty = form.getFieldValue('warranty_expiry_date');
    const audit = form.getFieldValue('last_audit_date');
  }, [form, form.getFieldValue('purchase_date'), form.getFieldValue('warranty_expiry_date'), form.getFieldValue('last_audit_date')]);

  const [selectedMaintenanceTypes, setSelectedMaintenanceTypes] = useState<number[]>([]);
  const [maintenanceCycles, setMaintenanceCycles] = useState<Record<string, number>>({});
  const [maintenanceTypes, setMaintenanceTypes] = useState<any[]>([]);
  
  // Thêm state để lưu trữ accessories và tính toán giá trị
  const [accessoriesList, setAccessoriesList] = useState<any[]>([]);
  const [hasAccessories, setHasAccessories] = useState(false);
  
  // Hàm tính tổng giá trị accessories
  const calculateTotalAccessoriesValue = (accessories: any[]): number => {
    return accessories.reduce((total, accessory) => total + (Number(accessory.total_price) || 0), 0);
  };
  
  // Hàm cập nhật giá trị tài sản khi accessories thay đổi
  const updateAssetValue = (accessories: any[]) => {
    const totalValue = calculateTotalAccessoriesValue(accessories);
    form.setFieldsValue({ purchase_cost: totalValue });
  };



  // Lấy danh sách loại bảo dưỡng khi mở form
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/maintenance-types`);
        setMaintenanceTypes(res.data);
      } catch {}
    })();
  }, [API_URL]);

  // Khi load asset, nếu có accessories thì set hasAccessories true
  useEffect(() => {
    if (accessoriesList && accessoriesList.length > 0) {
      setHasAccessories(true);
    }
  }, [accessoriesList]);

  // Khi load asset hoặc đổi công ty, lọc lại danh sách nhân viên
  useEffect(() => {
    if (asset && asset.company_id) {
      setCurrentLocationList(filterListByCompany(props.locationList, asset.company_id));
      setCurrentEmployeeList(filterListByCompany(props.employeeList, asset.company_id));
    }
  }, [asset, props.locationList, props.employeeList]);

  return (
    <Spin spinning={loading} tip="Đang xử lý...">
      {asset ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
                  onFinishFailed={({ errorFields }: { errorFields: any[] }) => {
          if (!errorFields || errorFields.length === 0) return;
          const field = errorFields[0]?.name?.[0];
          const tabMap = getTabMapForValidation();
          setActiveTabKey(tabMap[String(field)] || '1');
        }}
          initialValues={{
            technical_specifications: ['', '', '', '0', '0']
          }}
        >
          <Tabs 
            activeKey={activeTabKey}
            onChange={setActiveTabKey}
            defaultActiveKey="1"
            type="card"
            size="large"
            style={{ 
              backgroundColor: 'transparent',
              padding: '0'
            }}
            tabBarStyle={{
              marginBottom: '24px',
              borderBottom: '2px solid #f0f0f0'
            }}
            tabBarGutter={8}
          >
            <Tabs.TabPane 
              tab={
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <InfoCircleOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                  Thông tin chung
                </span>
              } 
              key="1"
            >
              <Row gutter={24}>
                <Col span={24}>
                  <Card 
                    title={
                      <span>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Thông tin cơ bản
                      </span>
                    }
                    size="small"
                    style={{ 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                    headStyle={{
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      fontWeight: '600'
                    }}
                    bodyStyle={{
                      padding: '24px'
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="company_id" 
                          label="Công ty" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Select
                            placeholder="Chọn công ty"
                            disabled
                          >
                            {props.companyList.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="asset_code" 
                          label="Mã tài sản" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Input disabled={user?.role !== 'super_admin'} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="name" 
                          label="Tên tài sản" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="category_id" 
                          label="Loại tài sản" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Select 
                            placeholder="Chọn loại tài sản" 
                            allowClear 
                            showSearch 
                            optionFilterProp="children"
                            dropdownRender={(menu) => (
                              <div>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                  <Button
                                    type="text"
                                    icon={<span>+</span>}
                                    onClick={() => setCategoryModalVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    + Tạo loại tài sản mới
                                  </Button>
                                </div>
                              </div>
                            )}
                          >
                            {props.categoryList.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="status" 
                          label="Trạng thái" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Select placeholder="Chọn trạng thái...">
                            <Select.Option value="pending_deployment">Chờ triển khai</Select.Option>
                            <Select.Option value="in_use">Đang sử dụng</Select.Option>
                            <Select.Option value="in_storage">Lưu kho</Select.Option>
                            <Select.Option value="under_maintenance">Bảo trì/Sửa chữa</Select.Option>
                            <Select.Option value="disposed">Đã thanh lý</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="location_id" 
                          label="Vị trí" 
                          rules={[{ required: true, message: 'Bắt buộc' }]}
                          style={{ marginBottom: '16px' }}
                        > 
                          <Select 
                            placeholder="Chọn vị trí" 
                            allowClear 
                            showSearch 
                            optionFilterProp="children"
                            dropdownRender={(menu) => (
                              <div>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                  <Button
                                    type="text"
                                    icon={<span>+</span>}
                                    onClick={() => setLocationModalVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    + Tạo vị trí mới
                                  </Button>
                                </div>
                              </div>
                            )}
                          >
                            {currentLocationList.map(l => <Select.Option key={l.id} value={l.id}>{l.name}</Select.Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="technical_condition" 
                          label="Trạng thái kỹ thuật"
                          style={{ marginBottom: '16px' }}
                        >
                          <Select placeholder="Chọn tình trạng">
                            <Select.Option value="new">Mới</Select.Option>
                            <Select.Option value="good">Tốt</Select.Option>
                            <Select.Option value="average">Trung bình</Select.Option>
                            <Select.Option value="bad">Kém</Select.Option>
                            <Select.Option value="broken">Hỏng</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="parent_asset_id" 
                          label="Tài sản cha"
                          style={{ marginBottom: '16px' }}
                        >
                          <Select placeholder="Chọn tài sản cha" allowClear showSearch optionFilterProp="children">
                            {/* {assetList.map(asset => (
                              <Select.Option key={asset.id} value={asset.id}>
                                {asset.asset_code} - {asset.name}
                              </Select.Option>
                            ))} */}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Form.Item 
                          name="description" 
                          label="Mô tả"
                          style={{ marginBottom: '16px' }}
                        >
                          <Input.TextArea rows={4} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
              <Row gutter={24} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card 
                    title={
                      <span>
                        <ShopOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                        Thông tin khác
                      </span>
                    }
                    size="small"
                    style={{ 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                      borderRadius: '8px'
                    }}
                    headStyle={{
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      fontWeight: '600'
                    }}
                    bodyStyle={{
                      padding: '24px'
                    }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="assigned_to_employee_id" 
                          label="Nhân viên được giao"
                          style={{ marginBottom: '16px' }}
                        >
                          <Select 
                            placeholder="Chọn nhân viên" 
                            allowClear 
                            showSearch 
                            optionFilterProp="children"
                            dropdownRender={(menu) => (
                              <div>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                  <Button
                                    type="text"
                                    icon={<span>+</span>}
                                    onClick={() => setEmployeeModalVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    + Tạo nhân viên mới
                                  </Button>
                                </div>
                              </div>
                            )}
                          >
                            {currentEmployeeList.map(e => <Select.Option key={e.id} value={e.id}>{e.full_name}</Select.Option>)}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="supplier_id" 
                          label="Nhà cung cấp"
                          style={{ marginBottom: '16px' }}
                        >
                          <Select 
                            placeholder="Chọn nhà cung cấp" 
                            allowClear 
                            showSearch 
                            optionFilterProp="children"
                            dropdownRender={(menu) => (
                              <div>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                                  <Button
                                    type="text"
                                    icon={<span>+</span>}
                                    onClick={() => setSupplierModalVisible(true)}
                                    style={{ width: '100%', textAlign: 'left' }}
                                  >
                                    + Tạo nhà cung cấp mới
                                  </Button>
                                </div>
                              </div>
                            )}
                          >
                            {props.supplierList.map(sup => (
                              <Select.Option key={sup.id} value={sup.id}>
                                {sup.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="purchase_cost" 
                          label="Giá mua"
                          style={{ marginBottom: '16px' }}
                          extra={accessoriesList.length > 0 ? "Giá trị được tính tự động từ tổng giá trị vật tư/thiết bị kèm theo" : ""}
                        >
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0}
                            placeholder="Nhập giá mua"
                            formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                            parser={(value: any) => value ? value.replace(/\D/g, '') : undefined}
                            disabled={accessoriesList.length > 0}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="purchase_date" 
                          label="Ngày mua"
                          style={{ marginBottom: '16px' }}
                        >
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            onChange={(date) => {
                              const warrantyMonths = form.getFieldValue('warranty_period_months');
                              if (date && warrantyMonths) {
                                const expiryDate = dayjs(date).add(warrantyMonths, 'month');
                                form.setFieldsValue({ warranty_expiry_date: expiryDate });
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="warranty_period_months" 
                          label="Thời hạn bảo hành (tháng)"
                          style={{ marginBottom: '16px' }}
                        >
                          <InputNumber 
                            style={{ width: '100%' }} 
                            min={0} 
                            onChange={(months) => {
                              const purchaseDate = form.getFieldValue('purchase_date');
                              if (purchaseDate && months && months > 0) {
                                const expiryDate = dayjs(purchaseDate).add(months, 'month');
                                form.setFieldsValue({ warranty_expiry_date: expiryDate });
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="warranty_expiry_date" 
                          label="Ngày hết hạn bảo hành"
                          style={{ marginBottom: '16px' }}
                        >
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            disabled 
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="last_audit_date" 
                          label="Ngày kiểm kê gần nhất" 
                          style={{ marginBottom: '16px' }}
                        >
                          <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            disabled={true}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item 
                          name="audit_cycle_months" 
                          label="Chu kỳ kiểm kê (tháng)"
                          style={{ marginBottom: '16px' }}
                        >
                          <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="has_accessories" valuePropName="checked">
                      <Checkbox checked={hasAccessories} onChange={e => setHasAccessories(e.target.checked)}>
                        Tài sản có vật tư/thiết bị kèm theo
                      </Checkbox>
                    </Form.Item>

                  </Card>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane 
              tab={
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <SettingOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                  Thông số kỹ thuật
                </span>
              } 
              key="2"
            >
              <Form.List name="technical_specifications" key={asset?.id || 'new'}>
                {(fields) => {

                  return (
                    <div>
                      {fields.slice(0, 5).map(({ key, name, ...restField }, idx) => (
                          <Form.Item
                            key={`tech-field-${idx}`}
                            label={defaultLabels[idx]}
                            name={name}
                            initialValue={''}
                            rules={[
                              (idx === 3 || idx === 4) ? { pattern: /^\d*$/, message: 'Chỉ nhập số!' } : {},
                            ]}
                            style={{ marginBottom: 12 }}
                          >
                          {idx === 2 ? (
                            <Input.TextArea
                              placeholder={defaultLabels[idx]}
                              rows={3}
                            />
                          ) : (
                            <Input
                              placeholder={defaultLabels[idx]}
                              type={idx === 3 || idx === 4 ? 'number' : 'text'}
                              min={idx === 3 || idx === 4 ? 0 : undefined}
                            />
                          )}
                        </Form.Item>
                      ))}
                    </div>
                  );
                }}
              </Form.List>
            </Tabs.TabPane>
            <Tabs.TabPane 
              tab={
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <ToolOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                  Bảo dưỡng
                </span>
              } 
              key="3"
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} lg={12}>
                  <Card 
                    title="Loại bảo dưỡng"
                    size="small"
                    style={{ 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                      borderRadius: '8px'
                    }}
                    headStyle={{
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      fontWeight: '600'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    <Form.Item label="Chọn loại bảo dưỡng">
                      <Select
                        mode="multiple"
                        placeholder="Chọn loại bảo dưỡng"
                        value={selectedMaintenanceTypes}
                        onChange={value => {
                          setSelectedMaintenanceTypes(value);
                          setMaintenanceCycles((prev: Record<string, number>) => {
                            const newCycles = { ...prev };
                            Object.keys(newCycles).forEach((k) => {
                              const keyNum = parseInt(k, 10);
                              if (!value.includes(keyNum)) {
                                delete newCycles[k];
                              }
                            });
                            return newCycles;
                          });
                        }}
                        style={{ width: '100%' }}
                        optionFilterProp="children"
                        showSearch
                      >
                        {maintenanceTypes.map(mt => (
                          <Select.Option key={mt.id} value={mt.id}>{mt.name}</Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card 
                    title="Chu kỳ bảo dưỡng"
                    size="small"
                    style={{ 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                      borderRadius: '8px'
                    }}
                    headStyle={{
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #f0f0f0',
                      fontWeight: '600'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    {selectedMaintenanceTypes.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        color: '#999', 
                        padding: '20px',
                        fontStyle: 'italic'
                      }}>
                        Vui lòng chọn loại bảo dưỡng ở cột bên trái
                      </div>
                    ) : (
                      selectedMaintenanceTypes.map(typeId => {
                        const mt = maintenanceTypes.find(mt => mt.id === typeId);
                        return (
                          <Form.Item
                            key={typeId}
                            label={`${mt?.name || 'Loại bảo dưỡng'}`}
                            required
                            validateStatus={
                              maintenanceCycles[String(typeId)] && maintenanceCycles[String(typeId)]! > 0 ? undefined : 'error'
                            }
                            help={
                              maintenanceCycles[String(typeId)] && maintenanceCycles[String(typeId)]! > 0
                                ? ''
                                : 'Vui lòng nhập chu kỳ (>=1 tháng)'
                            }
                            style={{ marginBottom: '12px' }}
                          >
                            <Input
                              type="number"
                              min={1}
                              style={{ width: '100%' }}
                              value={maintenanceCycles[String(typeId)] || ''}
                              onChange={e => {
                                const v = parseInt(e.target.value, 10);
                                setMaintenanceCycles(prev => ({ ...prev, [typeId]: isNaN(v) ? undefined : v }));
                              }}
                              placeholder="Nhập chu kỳ (tháng)"
                              addonAfter="tháng"
                            />
                          </Form.Item>
                        );
                      })
                    )}
                  </Card>
                </Col>
              </Row>
            </Tabs.TabPane>
            {hasAccessories && (
              <Tabs.TabPane 
                tab={<span><AppstoreAddOutlined style={{ marginRight: 8, fontSize: '16px' }} />Vật tư / Thiết bị</span>}
                key="5"
              >
                <div>
                  <AssetAccessories 
                    value={accessoriesList}
                    onChange={setAccessoriesList}
                    readOnly={false}
                  />
                  {accessoriesList.length > 0 && (
                    <div style={{ 
                      marginTop: 16, 
                      padding: 12, 
                      backgroundColor: '#f6ffed', 
                      border: '1px solid #b7eb8f', 
                      borderRadius: 6,
                      textAlign: 'center'
                    }}>
                      <strong>Tổng giá trị vật tư/thiết bị: {calculateTotalAccessoriesValue(accessoriesList).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} VNĐ</strong>
                    </div>
                  )}
                </div>
              </Tabs.TabPane>
            )}
            <Tabs.TabPane 
              tab={
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: '500',
                  fontSize: '14px'
                }}>
                  <PaperClipOutlined style={{ marginRight: 8, fontSize: '16px' }} />
                  Hình ảnh & hồ sơ
                </span>
              } 
              key="4"
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} lg={12}>
                  <Form.Item label="Ảnh tài sản">
                    <Upload
                      customRequest={handleImageUpload}
                      onRemove={handleRemoveImage}
                      fileList={imageFiles}
                      onChange={({ fileList }) => setImageFiles(fileList)}
                      listType="picture"
                      maxCount={5}
                      accept="image/*"
                    >
                      <Button>Chọn ảnh</Button>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Tài liệu đính kèm">
                    <Upload
                      customRequest={handleDocUpload}
                      onRemove={handleRemoveDoc}
                      fileList={docFiles}
                      onChange={({ fileList }) => setDocFiles(fileList)}
                      maxCount={5}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    >
                      <Button>Chọn file</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>
          <Form.Item style={{ textAlign: 'right', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                <Button onClick={handleCancel}>Hủy</Button>
                <Button type="primary" htmlType="submit">Cập nhật</Button>
              </div>
            </div>
          </Form.Item>
        </Form>
      ) : null}
      {/* Modal tạo nhà cung cấp mới, vị trí mới, loại tài sản mới, nhân viên mới giữ nguyên như cũ */}
      <SupplierModal
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        onSuccess={handleSupplierCreated}
      />
      <LocationModal
        visible={locationModalVisible}
        onCancel={() => setLocationModalVisible(false)}
        onSuccess={handleLocationCreated}
        companies={props.companyList}
        locations={currentLocationList}
        isSuperAdmin={user?.role === 'super_admin'}
        currentUser={user}
      />
      <AssetCategoryModal
        visible={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        onSuccess={handleCategoryCreated}
      />
      <EmployeeModal
        visible={employeeModalVisible}
        onCancel={() => setEmployeeModalVisible(false)}
        onSuccess={handleEmployeeCreated}
        companies={props.companyList}
        isSuperAdmin={user?.role === 'super_admin'}
        currentUser={user}
        hideFields={user?.role === 'super_admin' ? [] : ['company_id']}
      />
    </Spin>
  );
}

export default AssetEditForm; 